import type { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes.ts';
import type { AudiencePricing } from '@openx/types/targeting/audience.ts';
import type { ContextualPricingApi } from '@openx/types/targeting/contextualPricing.ts';
import { toNumber } from '@openx/utils/lib/toNumber.ts';
import type { PricingConfig } from '../OrganizationPricingItem.tsx';
import { createPricingConfig } from './pricingCalculations.ts';

export type FeeSummary = {
  audience: PricingConfig | null;
  contextual: PricingConfig | null;
};

type ContextualTotals = { priceCpm: number; pricePom: number };

// a missing or invalid total is NaN, so it is told apart from a real zero
const toTotal = (value: string | null | undefined): number => toNumber(value, { fallback: Number.NaN });

/**
 * Totals of the contextual fees as the API returns them (EXCH-19139). They are not worked out here: the API owns the
 * calculation, so without (valid) `totals` there is no contextual summary, while the per-partner fees still show.
 */
export const getContextualTotals = (contextualPricing?: ContextualPricingApi | null): ContextualTotals | null => {
  const priceCpm = toTotal(contextualPricing?.totals?.price_cpm);
  const pricePom = toTotal(contextualPricing?.totals?.price_pom);

  if (Number.isNaN(priceCpm) || Number.isNaN(pricePom)) return null;

  return { priceCpm, pricePom };
};

type CalculateFeeSummaryParams = {
  segmentPartnerRevenueMethod: SegmentPartnerRevenueMethod;
  /** Audience `pricing.totals` as the API returns them (already average/sum depending on the operator). */
  audienceTotals?: AudiencePricing['totals'] | null;
  contextualPricing?: ContextualPricingApi | null;
};

/** Audience and contextual fee summaries; the one without totals is null. */
export const calculateFeeSummary = ({
  segmentPartnerRevenueMethod,
  audienceTotals,
  contextualPricing,
}: CalculateFeeSummaryParams): FeeSummary => {
  const contextualTotals = getContextualTotals(contextualPricing);

  return {
    audience: audienceTotals
      ? createPricingConfig(segmentPartnerRevenueMethod, audienceTotals.price_cpm, audienceTotals.price_pom)
      : null,
    contextual: contextualTotals
      ? createPricingConfig(segmentPartnerRevenueMethod, contextualTotals.priceCpm, contextualTotals.pricePom)
      : null,
  };
};
